/**
 * 生成した動画をユーザーの手元へ渡す経路。
 *
 * タッチ端末で Web Share API が使えるときは OS の共有シートを開き、
 * それ以外は従来どおり直接ダウンロードする（ADR-0010）。
 * ファイル名と MIME、`share()` を同期に呼ぶ制約、キャンセルの扱いは
 * すべてこのファイルに閉じ込め、呼び出し側は経路の違いを持たない。
 */

export const VIDEO_MIME_TYPE = 'video/mp4';

/** 判定と受け渡しが触る環境。テストではここを差し替える */
export interface DeliveryEnvironment {
  canShare?: (data: { files: File[] }) => boolean;
  share?: (data: { files: File[] }) => Promise<void>;
  matchMedia?: (query: string) => { matches: boolean };
  createFile: (blob: Blob, fileName: string) => File;
  download: (blob: Blob, fileName: string) => void;
}

/** 動画のファイル名。生成時刻から MMDDHHmmss_video.mp4 を作る */
export function buildVideoFileName(now: Date = new Date()): string {
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  return `${mm}${dd}${hh}${min}${ss}_video.mp4`;
}

/** `<a download>` で直接ダウンロードする。共有が使えないときの経路であり、共有が拒まれたときの受け皿でもある */
export function downloadVideoFile(videoBlob: Blob, fileName: string = buildVideoFileName()): void {
  const url = URL.createObjectURL(videoBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/** 実行中のブラウザ。既定の環境 */
function browserEnvironment(): DeliveryEnvironment {
  return {
    canShare:
      typeof navigator !== 'undefined' && typeof navigator.canShare === 'function'
        ? (data) => navigator.canShare(data)
        : undefined,
    share:
      typeof navigator !== 'undefined' && typeof navigator.share === 'function'
        ? (data) => navigator.share(data)
        : undefined,
    matchMedia: typeof matchMedia === 'function' ? (query) => matchMedia(query) : undefined,
    createFile: (blob, fileName) => new File([blob], fileName, { type: VIDEO_MIME_TYPE }),
    download: downloadVideoFile,
  };
}

/**
 * 共有シートで保存する経路が使えるか。
 *
 * ブラウザ名や UA では判定しない。動画ファイルを共有できること
 * （`canShare({ files })`）と、指で触る端末であること（`pointer: coarse`）の
 * 二つだけを見る。共有シートを持っていてもマウスの端末では、
 * 直接ダウンロードのほうが手数が少ない。
 */
export function canShareVideoFile(env: DeliveryEnvironment = browserEnvironment()): boolean {
  if (!env.canShare || !env.share || !env.matchMedia) return false;
  if (!env.matchMedia('(pointer: coarse)').matches) return false;

  // 判定に実体は要らない。空のファイルで対応可否だけを尋ねる
  try {
    return env.canShare({ files: [env.createFile(new Blob([]), buildVideoFileName())] });
  } catch {
    return false;
  }
}

/** 共有シートを閉じてキャンセルされたのか、共有そのものが通らなかったのかを分ける */
function isShareCancelled(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * 動画をユーザーの手元へ渡す。共有が使えるなら共有シート、使えないならダウンロード。
 *
 * `navigator.share()` はタップのハンドラの中から同期に呼ぶ必要がある
 * （iOS Safari は操作と呼び出しの間に非同期の待ちが挟まると拒否する）。
 * この関数は `await` を挟まずに `share()` へ到達するので、呼び出し側は
 * イベントハンドラから直に呼ぶ限りその制約を守れる。
 *
 * ユーザーが共有シートを閉じた場合（AbortError）は何もしない。
 * それ以外の失敗はダウンロードへ落とす。
 */
export function deliverVideo(
  videoBlob: Blob,
  env: DeliveryEnvironment = browserEnvironment()
): void {
  const fileName = buildVideoFileName();

  if (!canShareVideoFile(env) || !env.share) {
    env.download(videoBlob, fileName);
    return;
  }

  const file = env.createFile(videoBlob, fileName);
  env.share({ files: [file] }).catch((error: unknown) => {
    if (isShareCancelled(error)) return;
    env.download(videoBlob, fileName);
  });
}
